import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { MapPin, Plus, Trash2, Save, Loader2, Target, Search, Pencil, X, CalendarOff, Ban, PlusCircle } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import ServiceMap from "@/components/ServiceMap";

function BlockedDates() {
  const [blocked, setBlocked] = useState([]);
  const [capacity, setCapacity] = useState(8);
  const [overrides, setOverrides] = useState({});
  const [date, setDate] = useState("");
  const [capDate, setCapDate] = useState("");
  const [capValue, setCapValue] = useState(8);
  const load = useCallback(async () => {
    const { data } = await api.get("/blocked-dates");
    setBlocked(data.blocked_dates || []); setCapacity(data.capacity || 8);
    setOverrides(data.capacity_overrides || {}); setCapValue(data.capacity || 8);
  }, []);
  useEffect(() => { load(); }, [load]);
  const toggle = async (d) => {
    if (!d) return;
    try { const { data } = await api.post("/driver/blocked-dates", { date: d }); setBlocked(data.blocked_dates); setDate(""); toast.success(`Date ${data.action}`); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const saveCapacity = async (d, val) => {
    if (!d) { toast.error("Pick a date first"); return; }
    try {
      const { data } = await api.post("/driver/day-capacity", { date: d, capacity: Number(val) });
      setOverrides(data.capacity_overrides);
      toast.success(`${d}: capacity set to ${data.capacity} loads`);
      setCapDate(""); setCapValue(capacity);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const overrideList = Object.entries(overrides).sort((a, b) => a[0].localeCompare(b[0]));
  return (
    <div className="bg-white rounded-2xl border border-slate-100 p-5" data-testid="blocked-dates-panel">
      <h3 className="font-heading font-bold text-slate-900 mb-1 flex items-center gap-2"><CalendarOff className="w-5 h-5 text-blue-600" /> Booking Availability</h3>
      <p className="text-xs text-slate-500 mb-4">Default capacity is <span className="font-semibold">{capacity} loads/day</span>. Block a day to turn bookings off, or open extra slots on a busy day.</p>

      <div className="flex items-end gap-2 mb-4">
        <div className="flex-1">
          <label className="text-xs font-medium text-slate-600 mb-1 block">Block a date</label>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="block-date-input"
            className="w-full border border-slate-200 rounded-lg px-3 py-2 outline-none focus:border-blue-500 text-sm" />
        </div>
        <button onClick={() => toggle(date)} className="brand-gradient text-white font-semibold px-4 py-2 rounded-lg text-sm flex items-center gap-1" data-testid="block-date-btn"><Ban className="w-4 h-4" /> Block</button>
      </div>
      <div className="space-y-2 mb-5">
        {blocked.length === 0 && <p className="text-sm text-slate-400 text-center py-2">No blocked days — all open.</p>}
        {blocked.map((d) => (
          <div key={d} className="flex items-center justify-between border border-red-100 bg-red-50/50 rounded-xl px-4 py-2.5" data-testid={`blocked-${d}`}>
            <span className="text-sm font-medium text-red-700 flex items-center gap-2"><CalendarOff className="w-4 h-4" /> {d}</span>
            <button onClick={() => toggle(d)} className="text-xs font-semibold text-blue-600 hover:underline" data-testid={`unblock-${d}`}>Re-open</button>
          </div>
        ))}
      </div>

      <div className="border-t border-slate-100 pt-4">
        <div className="flex items-center gap-2 mb-1"><PlusCircle className="w-4 h-4 text-blue-600" /><span className="font-semibold text-sm text-slate-800">Open more slots for a day</span></div>
        <p className="text-xs text-slate-500 mb-3">Finished early? Raise a day's load limit above {capacity} to take on more deliveries.</p>
        <div className="flex items-end gap-2 mb-3">
          <div className="flex-1">
            <label className="text-xs font-medium text-slate-600 mb-1 block">Date</label>
            <input type="date" value={capDate} onChange={(e) => setCapDate(e.target.value)} data-testid="capacity-date-input"
              className="w-full border border-slate-200 rounded-lg px-3 py-2 outline-none focus:border-blue-500 text-sm" />
          </div>
          <div className="w-24">
            <label className="text-xs font-medium text-slate-600 mb-1 block">Loads</label>
            <input type="number" min="1" max="30" value={capValue} onChange={(e) => setCapValue(e.target.value)} data-testid="capacity-value-input"
              className="w-full border border-slate-200 rounded-lg px-3 py-2 outline-none focus:border-blue-500 text-sm" />
          </div>
          <button onClick={() => saveCapacity(capDate, capValue)} className="brand-gradient text-white font-semibold px-4 py-2 rounded-lg text-sm flex items-center gap-1" data-testid="capacity-save-btn"><PlusCircle className="w-4 h-4" /> Set</button>
        </div>
        <div className="space-y-2">
          {overrideList.length === 0 && <p className="text-sm text-slate-400 text-center py-1">No custom limits — every open day allows {capacity} loads.</p>}
          {overrideList.map(([d, cap]) => (
            <div key={d} className="flex items-center justify-between border border-blue-100 bg-blue-50/50 rounded-xl px-4 py-2.5" data-testid={`override-${d}`}>
              <span className="text-sm font-medium text-blue-700 flex items-center gap-2"><PlusCircle className="w-4 h-4" /> {d} — <span className="font-bold">{cap} loads</span></span>
              <button onClick={() => saveCapacity(d, capacity)} className="text-xs font-semibold text-slate-500 hover:underline" data-testid={`override-reset-${d}`}>Reset to {capacity}</button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function ServiceAreaManager() {
  const [config, setConfig] = useState(null);
  const [depot, setDepot] = useState({ label: "", lat: 0, lng: 0, service_radius_km: 45 });
  const [savingDepot, setSavingDepot] = useState(false);
  const [query, setQuery] = useState("");
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({ name: "", town_water: "", pure_water: "" });
  const [editId, setEditId] = useState(null);
  const [editDraft, setEditDraft] = useState({ name: "", town_water: "", pure_water: "" });

  const load = useCallback(async () => {
    const { data } = await api.get("/service/config");
    setConfig(data);
    setDepot({ label: data.depot.label, lat: data.depot.lat, lng: data.depot.lng, service_radius_km: data.service_radius_km });
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveDepot = async () => {
    setSavingDepot(true);
    try {
      await api.put("/service/config", {
        label: depot.label, lat: Number(depot.lat), lng: Number(depot.lng),
        service_radius_km: Number(depot.service_radius_km),
      });
      toast.success("Depot & coverage saved");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSavingDepot(false); }
  };

  const addZone = async () => {
    if (!draft.name) { toast.error("Suburb name required"); return; }
    try {
      await api.post("/service/zones", { name: draft.name, town_water: Number(draft.town_water) || 0, pure_water: Number(draft.pure_water) || 0 });
      toast.success("Suburb added");
      setDraft({ name: "", town_water: "", pure_water: "" }); setAdding(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const saveEdit = async (id) => {
    try {
      await api.patch(`/service/zones/${id}`, { name: editDraft.name, town_water: Number(editDraft.town_water) || 0, pure_water: Number(editDraft.pure_water) || 0 });
      toast.success("Rate updated"); setEditId(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const removeZone = async (id) => {
    try { await api.delete(`/service/zones/${id}`); toast.success("Removed"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!config) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div>;

  const zones = (config.zones || []).filter((z) => z.name.toLowerCase().includes(query.toLowerCase()));

  return (
    <div className="space-y-6">
      <BlockedDates />
      <div className="grid lg:grid-cols-[380px_1fr] gap-6 items-start">
      {/* MAP + DEPOT */}
      <div className="bg-white rounded-2xl border border-slate-100 p-5">
        <h3 className="font-heading font-bold text-slate-900 mb-1 flex items-center gap-2">
          <Target className="w-5 h-5 text-blue-600" /> Depot & Coverage
        </h3>
        <p className="text-xs text-slate-500 mb-4">Click the map to move your depot. The ring shows your coverage radius.</p>
        <ServiceMap
          depot={{ label: depot.label, lat: Number(depot.lat), lng: Number(depot.lng) }}
          serviceRadiusKm={Number(depot.service_radius_km)} bands={[]} height={240}
          onPick={(lat, lng) => setDepot({ ...depot, lat: lat.toFixed(6), lng: lng.toFixed(6) })}
        />
        <div className="space-y-3 mt-4">
          <Input label="Depot label" value={depot.label} onChange={(v) => setDepot({ ...depot, label: v })} testid="depot-label" />
          <div className="grid grid-cols-2 gap-3">
            <Input label="Latitude" value={depot.lat} onChange={(v) => setDepot({ ...depot, lat: v })} testid="depot-lat" />
            <Input label="Longitude" value={depot.lng} onChange={(v) => setDepot({ ...depot, lng: v })} testid="depot-lng" />
          </div>
          <Input label="Coverage radius (km)" value={depot.service_radius_km} onChange={(v) => setDepot({ ...depot, service_radius_km: v })} testid="depot-radius" />
        </div>
        <button onClick={saveDepot} disabled={savingDepot}
          className="brand-gradient btn-shine text-white font-semibold w-full py-2.5 rounded-full mt-4 flex items-center justify-center gap-2 disabled:opacity-60" data-testid="save-depot-btn">
          {savingDepot ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4" /> Save</>}
        </button>
      </div>

      {/* SUBURB RATES */}
      <div className="bg-white rounded-2xl border border-slate-100 p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading font-bold text-slate-900 flex items-center gap-2">
            <MapPin className="w-5 h-5 text-blue-600" /> Suburb Rates <span className="text-sm font-normal text-slate-400">({config.zones?.length || 0})</span>
          </h3>
          <button onClick={() => setAdding(!adding)} className="text-blue-600 hover:bg-blue-50 p-1.5 rounded-lg" data-testid="zone-add-btn"><Plus className="w-5 h-5" /></button>
        </div>

        <div className="flex items-center gap-2 border border-slate-200 rounded-xl px-3 mb-3">
          <Search className="w-4 h-4 text-slate-400" />
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search suburb..." data-testid="zone-search"
            className="w-full py-2 outline-none text-sm bg-transparent" />
        </div>

        {adding && (
          <div className="border border-blue-100 bg-blue-50/40 rounded-xl p-3 mb-3 grid grid-cols-[1fr_90px_90px_auto] gap-2 items-end" data-testid="zone-add-form">
            <Input label="Suburb" value={draft.name} onChange={(v) => setDraft({ ...draft, name: v })} testid="zone-name-input" />
            <Input label="Town $" type="number" value={draft.town_water} onChange={(v) => setDraft({ ...draft, town_water: v })} testid="zone-town-input" />
            <Input label="Pure $" type="number" value={draft.pure_water} onChange={(v) => setDraft({ ...draft, pure_water: v })} testid="zone-pure-input" />
            <button onClick={addZone} className="brand-gradient text-white font-semibold px-4 py-2 rounded-lg text-sm" data-testid="zone-save-btn">Add</button>
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-slate-400 uppercase tracking-wide border-b border-slate-100">
                <th className="py-2 pr-4 font-semibold">Suburb</th>
                <th className="py-2 px-3 font-semibold">Town Water</th>
                <th className="py-2 px-3 font-semibold">Pure Water</th>
                <th className="py-2 pl-3 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {zones.map((z) => (
                <tr key={z.id} className="border-b border-slate-50" data-testid={`zone-row-${z.id}`}>
                  {editId === z.id ? (
                    <>
                      <td className="py-2 pr-4"><input value={editDraft.name} onChange={(e) => setEditDraft({ ...editDraft, name: e.target.value })} data-testid={`zone-edit-name-${z.id}`} className="w-full border border-slate-200 rounded-lg px-2 py-1.5" /></td>
                      <td className="py-2 px-3"><input type="number" value={editDraft.town_water} onChange={(e) => setEditDraft({ ...editDraft, town_water: e.target.value })} data-testid={`zone-edit-town-${z.id}`} className="w-20 border border-slate-200 rounded-lg px-2 py-1.5" /></td>
                      <td className="py-2 px-3"><input type="number" value={editDraft.pure_water} onChange={(e) => setEditDraft({ ...editDraft, pure_water: e.target.value })} data-testid={`zone-edit-pure-${z.id}`} className="w-20 border border-slate-200 rounded-lg px-2 py-1.5" /></td>
                      <td className="py-2 pl-3 text-right whitespace-nowrap">
                        <button onClick={() => saveEdit(z.id)} className="text-green-600 p-1" data-testid={`zone-save-edit-${z.id}`}><Save className="w-4 h-4" /></button>
                        <button onClick={() => setEditId(null)} className="text-slate-400 p-1"><X className="w-4 h-4" /></button>
                      </td>
                    </>
                  ) : (
                    <>
                      <td className="py-2.5 pr-4 font-semibold text-slate-800">{z.name}</td>
                      <td className="py-2.5 px-3 text-slate-600">${z.town_water}</td>
                      <td className="py-2.5 px-3 text-slate-600">${z.pure_water}</td>
                      <td className="py-2.5 pl-3 text-right whitespace-nowrap">
                        <button onClick={() => { setEditId(z.id); setEditDraft({ name: z.name, town_water: z.town_water, pure_water: z.pure_water }); }} className="text-slate-400 hover:text-blue-600 p-1" data-testid={`zone-edit-${z.id}`}><Pencil className="w-4 h-4" /></button>
                        <button onClick={() => removeZone(z.id)} className="text-slate-300 hover:text-red-500 p-1" data-testid={`zone-delete-${z.id}`}><Trash2 className="w-4 h-4" /></button>
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
          {zones.length === 0 && <p className="text-sm text-slate-400 py-6 text-center">No suburbs match.</p>}
        </div>
      </div>
      </div>
    </div>
  );
}

const Input = ({ label, value, onChange, testid, type = "text" }) => (
  <div>
    <label className="text-xs font-medium text-slate-600 mb-1 block">{label}</label>
    <input type={type} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
      className="w-full border border-slate-200 rounded-lg px-3 py-2 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 text-sm" />
  </div>
);
